// Port of sfx.js — crystalline WebAudio. ES-module export.

let ctx = null;
let master = null;
let muted = typeof localStorage !== 'undefined' && localStorage.getItem('c-muted') === '1';
let lastPlay = 0;

function ensureCtx() {
  if (ctx) return ctx;
  try {
    ctx = new (window.AudioContext || window.webkitAudioContext)();
    master = ctx.createGain();
    master.gain.value = 0.22;
    master.connect(ctx.destination);
  } catch (e) { ctx = null; }
  return ctx;
}

function resume() {
  const c = ensureCtx();
  if (c && c.state === 'suspended') c.resume();
}

if (typeof window !== 'undefined') {
  ['pointerdown','keydown','touchstart'].forEach(ev => window.addEventListener(ev, resume, { passive: true }));
}

function note(freq, { dur = 0.22, type = 'sine', gain = 0.5, attack = 0.005, release = 0.12, detune = 0 } = {}) {
  const c = ensureCtx(); if (!c || muted) return;
  const now = c.currentTime;
  const osc = c.createOscillator();
  const g = c.createGain();
  osc.type = type; osc.frequency.value = freq; if (detune) osc.detune.value = detune;
  g.gain.setValueAtTime(0, now);
  g.gain.linearRampToValueAtTime(gain, now + attack);
  g.gain.exponentialRampToValueAtTime(0.0001, now + dur + release);
  osc.connect(g).connect(master);
  osc.start(now); osc.stop(now + dur + release + 0.02);
}

function chord(freqs, opts = {}) {
  freqs.forEach((f, i) => setTimeout(() => note(f, opts), i * (opts.stagger || 0)));
}

function noise({ dur = 0.18, gain = 0.08, filter = 1200 } = {}) {
  const c = ensureCtx(); if (!c || muted) return;
  const now = c.currentTime;
  const len = Math.floor(c.sampleRate * dur);
  const buf = c.createBuffer(1, len, c.sampleRate);
  const d = buf.getChannelData(0);
  for (let i = 0; i < len; i++) d[i] = (Math.random() * 2 - 1) * (1 - i / len);
  const src = c.createBufferSource(); src.buffer = buf;
  const bp = c.createBiquadFilter(); bp.type = 'bandpass'; bp.frequency.value = filter; bp.Q.value = 0.8;
  const g = c.createGain();
  g.gain.setValueAtTime(0, now); g.gain.linearRampToValueAtTime(gain, now + 0.01);
  g.gain.exponentialRampToValueAtTime(0.0001, now + dur);
  src.connect(bp).connect(g).connect(master);
  src.start(now);
}

function throttle(fn, ms = 30) {
  return (...args) => {
    const now = performance.now();
    if (now - lastPlay < ms) return;
    lastPlay = now; fn(...args);
  };
}

// Per-channel throttling — used so the global button click doesn't compete
// with throttled event sounds like sfx.nav or sfx.toast. Each named channel
// has its own `last` timestamp.
const channelLast = new Map();
function channelThrottle(fn, channel = 'default', ms = 30) {
  return (...args) => {
    const now = performance.now();
    const last = channelLast.get(channel) || 0;
    if (now - last < ms) return;
    channelLast.set(channel, now);
    fn(...args);
  };
}

// ──────────────────────────────────────────────────────────────────
// Loop helpers — used by `thinking` and `transcribing` ambient beds.
// A loop is a set of oscillators that fade in, sustain indefinitely,
// and fade out on stop(). The master loop gain ramps so start/stop
// is smooth and never audibly clicks.
// ──────────────────────────────────────────────────────────────────

function startLoop({ voices, bpm = 60, stepNotes = [], stepDur = 0.5, stepGain = 0.04, filterHz = 1200 }) {
  const c = ensureCtx();
  if (!c || muted) return { stop: () => {} };

  const now = c.currentTime;
  const bus = c.createGain();
  bus.gain.setValueAtTime(0, now);
  bus.gain.linearRampToValueAtTime(1, now + 0.8); // gentle fade-in
  const lowpass = c.createBiquadFilter();
  lowpass.type = 'lowpass';
  lowpass.frequency.value = filterHz;
  lowpass.Q.value = 0.6;
  bus.connect(lowpass).connect(master);

  // Sustained pad voices: detuned sine+triangle stack.
  const nodes = [];
  voices.forEach(({ freq, type = 'sine', gain = 0.035, detune = 0 }) => {
    const osc = c.createOscillator();
    const g = c.createGain();
    osc.type = type;
    osc.frequency.value = freq;
    osc.detune.value = detune;
    g.gain.value = gain;
    // Subtle chorus: a slow LFO on the gain so the pad breathes.
    const lfo = c.createOscillator();
    const lfoGain = c.createGain();
    lfo.frequency.value = 0.18 + Math.random() * 0.15;
    lfoGain.gain.value = gain * 0.5;
    lfo.connect(lfoGain).connect(g.gain);
    osc.connect(g).connect(bus);
    osc.start(now);
    lfo.start(now);
    nodes.push(osc, lfo);
  });

  // Optional stepwise arpeggio on top of the pad — chiming notes at
  // the configured BPM. Plays as long as the loop is alive.
  let stepTimer = null;
  if (stepNotes && stepNotes.length) {
    let i = 0;
    const stepMs = 60_000 / bpm;
    const fireStep = () => {
      const ci = ensureCtx(); if (!ci || muted) return;
      const t = ci.currentTime;
      const osc = ci.createOscillator();
      const g = ci.createGain();
      osc.type = 'sine';
      osc.frequency.value = stepNotes[i % stepNotes.length];
      g.gain.setValueAtTime(0, t);
      g.gain.linearRampToValueAtTime(stepGain, t + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, t + stepDur);
      osc.connect(g).connect(bus);
      osc.start(t);
      osc.stop(t + stepDur + 0.05);
      i++;
    };
    stepTimer = setInterval(fireStep, stepMs);
    // Fire first step almost immediately so start feels responsive.
    setTimeout(fireStep, 120);
  }

  return {
    stop() {
      const cc = ensureCtx(); if (!cc) return;
      const t = cc.currentTime;
      // Fade the bus out smoothly before tearing down voices.
      bus.gain.cancelScheduledValues(t);
      bus.gain.setValueAtTime(bus.gain.value, t);
      bus.gain.linearRampToValueAtTime(0, t + 0.6);
      if (stepTimer) { clearInterval(stepTimer); stepTimer = null; }
      setTimeout(() => {
        try { nodes.forEach(n => { try { n.stop(); } catch {} }); } catch {}
        try { bus.disconnect(); lowpass.disconnect(); } catch {}
      }, 700);
    },
  };
}

// Active loops by name so repeated start calls do not stack up.
const activeLoops = new Map();
function ensureSingletonLoop(name, factory) {
  if (activeLoops.has(name)) return activeLoops.get(name);
  const handle = factory();
  activeLoops.set(name, handle);
  return handle;
}
function stopSingletonLoop(name) {
  const h = activeLoops.get(name);
  if (h) { try { h.stop(); } catch {} activeLoops.delete(name); }
}

export const sfx = {
  check: throttle(() => {
    chord([880, 1108.73, 1318.51], { dur: 0.25, type: 'sine', gain: 0.32, release: 0.35, stagger: 18 });
    note(2637, { dur: 0.1, type: 'sine', gain: 0.08, release: 0.2 });
  }, 40),
  uncheck: throttle(() => {
    note(523.25, { dur: 0.12, type: 'triangle', gain: 0.22, release: 0.16 });
    setTimeout(() => note(392, { dur: 0.18, type: 'sine', gain: 0.14, release: 0.18 }), 30);
  }, 40),
  milestone: throttle(() => {
    const seq = [659.25, 783.99, 987.77, 1318.51, 1567.98];
    seq.forEach((f, i) => setTimeout(() => note(f, { dur: 0.3, type: 'sine', gain: 0.36, release: 0.4 }), i * 60));
    setTimeout(() => chord([1318.51, 1975.53, 2637], { dur: 0.5, gain: 0.2, release: 0.6 }), 320);
  }, 200),
  nav: throttle(() => {
    note(1568, { dur: 0.04, type: 'sine', gain: 0.18, release: 0.06 });
    noise({ dur: 0.06, gain: 0.025, filter: 2000 });
  }, 40),
  sheetOpen: throttle(() => {
    noise({ dur: 0.24, gain: 0.05, filter: 900 });
    const c = ensureCtx(); if (!c || muted) return;
    const now = c.currentTime;
    const osc = c.createOscillator(); const g = c.createGain();
    osc.type = 'sine'; osc.frequency.setValueAtTime(440, now); osc.frequency.exponentialRampToValueAtTime(880, now + 0.18);
    g.gain.setValueAtTime(0, now); g.gain.linearRampToValueAtTime(0.18, now + 0.02); g.gain.exponentialRampToValueAtTime(0.0001, now + 0.3);
    osc.connect(g).connect(master); osc.start(now); osc.stop(now + 0.35);
  }, 100),
  sheetClose: throttle(() => {
    const c = ensureCtx(); if (!c || muted) return;
    const now = c.currentTime;
    const osc = c.createOscillator(); const g = c.createGain();
    osc.type = 'sine'; osc.frequency.setValueAtTime(880, now); osc.frequency.exponentialRampToValueAtTime(440, now + 0.18);
    g.gain.setValueAtTime(0, now); g.gain.linearRampToValueAtTime(0.14, now + 0.02); g.gain.exponentialRampToValueAtTime(0.0001, now + 0.24);
    osc.connect(g).connect(master); osc.start(now); osc.stop(now + 0.28);
  }, 100),
  del: throttle(() => {
    note(196, { dur: 0.18, type: 'triangle', gain: 0.28, release: 0.22 });
    noise({ dur: 0.1, gain: 0.04, filter: 300 });
  }, 60),
  toast: throttle(() => {
    note(1046.5, { dur: 0.1, type: 'sine', gain: 0.18, release: 0.22 });
    setTimeout(() => note(1318.51, { dur: 0.12, type: 'sine', gain: 0.14, release: 0.22 }), 50);
  }, 80),
  month: throttle(() => { note(1320, { dur: 0.05, type: 'sine', gain: 0.16, release: 0.08 }); }, 40),
  greeting: throttle(() => {
    const seq = [523.25, 659.25, 783.99];
    seq.forEach((f, i) => setTimeout(() => note(f, { dur: 0.55, type: 'sine', gain: 0.18, release: 0.9 }), i * 140));
    setTimeout(() => note(1046.5, { dur: 0.6, type: 'sine', gain: 0.1, release: 1.0 }), 480);
  }, 300),
  briefingReady: throttle(() => {
    const seq = [659.25, 987.77, 1318.51];
    seq.forEach((f, i) => setTimeout(() => note(f, { dur: 0.4, type: 'sine', gain: 0.22, release: 0.5 }), i * 80));
    setTimeout(() => noise({ dur: 0.3, gain: 0.03, filter: 1600 }), 40);
  }, 250),
  tick: throttle(() => { note(2200, { dur: 0.02, type: 'sine', gain: 0.06, release: 0.04 }); }, 60),
  pulse: throttle(() => { note(659.25, { dur: 0.3, type: 'sine', gain: 0.06, release: 0.5 }); }, 200),

  // ── Click — fires on every button in the app via the global listener
  // installed in main.jsx. Light but audible on the iPhone speaker. The
  // two stacked sines give it a crystalline "tink" rather than a blunt
  // pop. Channel-throttled so it never doubles with nav/toast.
  click: channelThrottle(() => {
    note(1760, { dur: 0.035, type: 'sine',     gain: 0.42, release: 0.08, attack: 0.002 });
    note(2637, { dur: 0.055, type: 'sine',     gain: 0.22, release: 0.12, attack: 0.002 });
    note(880,  { dur: 0.03,  type: 'triangle', gain: 0.18, release: 0.05 });
  }, 'click', 22),

  // ── Thinking — ambient pad for brief generation. A slow C-major-9 stack
  // with a gentle arpeggio on top. Conveys calm research, not urgency.
  startThinking() {
    ensureSingletonLoop('thinking', () => startLoop({
      voices: [
        { freq: 261.63, type: 'sine',     gain: 0.030, detune: -4 }, // C4
        { freq: 329.63, type: 'sine',     gain: 0.026, detune: +3 }, // E4
        { freq: 392.00, type: 'triangle', gain: 0.022, detune: -2 }, // G4
        { freq: 493.88, type: 'sine',     gain: 0.018, detune: +4 }, // B4
        { freq: 587.33, type: 'sine',     gain: 0.014, detune: -3 }, // D5
      ],
      bpm: 54,
      stepNotes: [783.99, 987.77, 1174.66, 1318.51, 1174.66, 987.77], // G5 B5 D6 E6 D6 B5
      stepDur: 0.7,
      stepGain: 0.035,
      filterHz: 2400,
    }));
  },
  stopThinking() { stopSingletonLoop('thinking'); },

  // ── Transcribing — lighter, airier loop for the Whisper voice-to-text
  // wait. Higher register, gentler amplitude, no arpeggio — more of a
  // breath than a melody, so it does not distract while the user is
  // waiting for their own words to come back.
  startTranscribing() {
    ensureSingletonLoop('transcribing', () => startLoop({
      voices: [
        { freq: 523.25, type: 'sine', gain: 0.018, detune: -5 }, // C5
        { freq: 659.25, type: 'sine', gain: 0.015, detune: +4 }, // E5
        { freq: 880.00, type: 'sine', gain: 0.012, detune: -3 }, // A5
      ],
      bpm: 0,
      stepNotes: [],
      filterHz: 3200,
    }));
    // Also overlay a soft filtered noise breath so it sounds like a room.
    const c = ensureCtx(); if (!c || muted) return;
    (function breath() {
      if (!activeLoops.has('transcribing')) return;
      noise({ dur: 1.4, gain: 0.012, filter: 600 });
      setTimeout(breath, 2400 + Math.random() * 800);
    })();
  },
  stopTranscribing() { stopSingletonLoop('transcribing'); },

  // ── Narrating — ambient pad while ElevenLabs generates the TTS audio
  // for an Intel Brief. Warmer / lower than `thinking`, with a slight
  // breath, so the brief generation and the narration generation feel
  // like distinct moments. Uses a triadic stack with a gentle sub.
  startNarrating() {
    ensureSingletonLoop('narrating', () => startLoop({
      voices: [
        { freq: 174.61, type: 'sine',     gain: 0.026, detune: -3 }, // F3 (sub)
        { freq: 261.63, type: 'sine',     gain: 0.024, detune: +2 }, // C4
        { freq: 349.23, type: 'triangle', gain: 0.020, detune: -4 }, // F4
        { freq: 440.00, type: 'sine',     gain: 0.016, detune: +3 }, // A4
      ],
      bpm: 42,
      stepNotes: [659.25, 783.99, 1046.50, 783.99],   // E5 G5 C6 G5
      stepDur: 0.95,
      stepGain: 0.022,
      filterHz: 1800,
    }));
  },
  stopNarrating() { stopSingletonLoop('narrating'); },

  // ── Saving — quick rising pulse while we persist parsed goals (or any
  // small Claude-backed save). Different timbre from `thinking` so a user
  // who hears it back-to-back recognizes "we're committing" vs "we're
  // researching".
  startSaving() {
    ensureSingletonLoop('saving', () => startLoop({
      voices: [
        { freq: 392.00, type: 'sine',     gain: 0.022, detune: -2 }, // G4
        { freq: 523.25, type: 'sine',     gain: 0.018, detune: +3 }, // C5
        { freq: 659.25, type: 'triangle', gain: 0.014, detune: -3 }, // E5
      ],
      bpm: 96,                                       // brisker than thinking
      stepNotes: [880, 1046.5, 1318.5, 1046.5],      // A5 C6 E6 C6
      stepDur: 0.42,
      stepGain: 0.030,
      filterHz: 2600,
    }));
  },
  stopSaving() { stopSingletonLoop('saving'); },

  // ── Ready — when the brief or transcript finishes. Shorter, lusher
  // than briefingReady; used alongside it for richness.
  ready: throttle(() => {
    const seq = [523.25, 659.25, 783.99, 1046.5];
    seq.forEach((f, i) => setTimeout(() => note(f, { dur: 0.45, type: 'sine', gain: 0.18, release: 0.6 }), i * 60));
    setTimeout(() => chord([1046.5, 1318.51, 1567.98], { dur: 0.8, gain: 0.12, release: 1.0 }), 260);
  }, 300),

  setMuted(v) { muted = !!v; try { localStorage.setItem('c-muted', muted ? '1' : '0'); } catch {} },
  isMuted() { return muted; },
  toggle() { this.setMuted(!muted); return muted; },
};

// Keep window.sfx available for any legacy code paths that still read it.
if (typeof window !== 'undefined') window.sfx = sfx;
